import Navigation from "../../common/components/Navigation/Navigation";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { IProjectGroupWithProjects } from "../../features/projectGroups/state/projectGroupsSlice";
import {
  deleteProjectGroup,
  fetchAllCurrentElements,
  getAllProjectGroupsWithNestedElements,
  getCurrentModeListProjectGroup,
  getProjectGroupPermissionsFromServer,
  setCurrentModeListProjectGroup,
  setCurrentProjectGroupPermissions,
  setEdited as setEditedProjectGroup,
  setSelectedProjectGroup,
  setSelectedProjectGroupFromId,
  getSearchState,
  getCurrentSearchCount,
  fetchProjectGroupsWithProjects,
  setSearchPage,
} from "../../features/projectGroups/state/projectGroupsSlice";
import ProjectGroupList, {
  PROJECT_GROUP_LIST_COMPONENT_ID,
  PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
} from "../../features/projectGroups/components/ProjectGroupList";
import { useTranslation } from "react-i18next";
import handleThreeDotsButtonClickForProjectsAndProjectGroups from "../../common/util/handleThreeDotsButtonClickForProjectsAndProjectGroups";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import {
  fetchCurrentProjectGroupCompletion,
  fetchProjectGroupsCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import type IProjectGroupsCompletionItem from "../../features/enduserStats/dataTypes/IProjectGroupsCompletionItem";
import { ENDUSER_PROJECT_LIST_COMPONENT_ID } from "../../enduserPages/EnduserProjectsList/EnduserProjectsList";
import { ViewGridIcon, ViewListIcon } from "@heroicons/react/outline";
import ModeListProjectGroup from "./ModeListProjectAndProjectsGroup";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useLoading } from "src/common/components/AppState/IsLoading";
import { UPLOAD_BAR_HEIGHT } from "src/common/template/Layout";
import { getCurrentUploadsStateList } from "src/features/gallery/state/gallerySlice";
import { setAlert } from "../../features/alert/state/alertsSlice";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import { useGalleryPagination } from "src/features/gallery/useGalleryPagination";
import { trackEvent } from "src/trackEvent";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";

const ProjectGroupsList = ({ isEnduser = false }: { isEnduser?: boolean }) => {
  const [isCheck, setIsCheck] = useState<string[]>([]);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const enduserProjectGroups = useAppSelector(getEnduserProjectGroupsCompletion);
  const projectGroups = isEnduser ? enduserProjectGroups : [];
  const projectGroupsWithNestedElements = useAppSelector(getAllProjectGroupsWithNestedElements);

  const [clickedProjectGroupId, setClickedProjectGroupId] = useState(0);
  const [isFilteredByPublishedProjects, setIsFilteredByPublishedProjects] = useState(false);
  const modeListCurrentState = useAppSelector(getCurrentModeListProjectGroup);
  const currentUploadsStateList = useAppSelector(getCurrentUploadsStateList);

  const projectGroupSearch = useAppSelector(getSearchState);
  const { page, limit = 20 } = projectGroupSearch;
  const handlePageChange = useGalleryPagination({ mode: "projectGroup" });
  const loadingState = useLoading(PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID);
  const projectGroupsAreLoading =
    loadingState == LoadingState.Loading || loadingState == LoadingState.Idle;
  useTourView(
    "courses-list",
    loadingState !== LoadingState.Loading && projectGroupSearch.inFlightListUrl === null,
    !isEnduser && !modeListCurrentState && projectGroupsWithNestedElements.length > 0
      ? TOUR_VIEW_ANCHORS["courses-list"]
      : [],
  );

  useEffect(() => {
    if (isEnduser) {
      if (!projectGroups || projectGroups.length === 0) {
        void dispatch(
          fetchProjectGroupsCompletion({ componentId: PROJECT_GROUP_LIST_COMPONENT_ID }),
        );
      }
    }
  }, [location.key]);

  const handleClick = (projectGroup: IProjectGroupWithProjects | IProjectGroupsCompletionItem) => {
    if (isEnduser) {
      trackEvent("enduser-course-open");
      const pg = projectGroup as IProjectGroupsCompletionItem;
      void dispatch(
        fetchCurrentProjectGroupCompletion({
          componentId: ENDUSER_PROJECT_LIST_COMPONENT_ID,
          buildId: pg.latestVersion.buildId,
        }),
      );

      navigate(`../courses/${pg.projectGroupId}/list`);
    } else {
      const pg = projectGroup as IProjectGroupWithProjects;
      void dispatch(
        fetchAllCurrentElements({
          projectGroupId: Number(pg.id),
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      );
      dispatch(setSelectedProjectGroup(pg));
      void dispatch(getProjectGroupPermissionsFromServer({ projectGroupId: Number(pg.id) })).then(
        (res) => {
          if (getProjectGroupPermissionsFromServer.fulfilled.match(res)) {
            dispatch(setCurrentProjectGroupPermissions(res.payload.data));
          }
        },
      );

      navigate(`../courses/${pg.id}/list`);
    }
  };

  const onChangeGridMedia = () => {
    dispatch(setCurrentModeListProjectGroup(!modeListCurrentState));
  };

  const setEditedProjectGroupId = (id: number, projectGroup: IProjectGroupWithProjects) => {
    const pg = projectGroup;
    setClickedProjectGroupId(id);
    void dispatch(
      fetchAllCurrentElements({
        projectGroupId: pg.id,
      }),
    );
    dispatch(setSelectedProjectGroup(pg));
    dispatch(setEditedProjectGroup());
  };

  const totalProjectGroups = useAppSelector(getCurrentSearchCount) ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalProjectGroups / limit));

  return (
    <>
      <Navigation
        title={t("general.projectGroups")}
        showSearchBar={!isEnduser}
        isFilteredByPublishedProjects={isFilteredByPublishedProjects}
        setIsFilteredByPublishedProjects={setIsFilteredByPublishedProjects}
      />

      <div className="fixed top-20 z-50 right-2 mr-5">
        <button onClick={onChangeGridMedia}>
          {!isEnduser &&
            (modeListCurrentState ? (
              <ViewGridIcon className="w-7 h-7 stroke-green-500 mr-3" />
            ) : (
              <ViewListIcon className="w-7 h-7 stroke-green-500 mr-3" />
            ))}
        </button>
      </div>

      {modeListCurrentState ? (
        <div
          className={`overflow-y-auto personalize-scroll-visible ${
            isCheck.length > 0 ? "mt-10" : ""
          } `}
        >
          <div className="block w-full overflow-y-auto personalize-scroll-visible p-2">
            <ModeListProjectGroup
              setEditedProjectGroupId={setEditedProjectGroupId}
              setIsCheck={setIsCheck}
              isCheck={isCheck}
              arrayItemsForList={isEnduser ? [] : projectGroupsWithNestedElements}
              setClickedProjectGroupId={setClickedProjectGroupId}
              handleClick={handleClick}
              clickedProjectGroupId={clickedProjectGroupId}
              isProjectGroup={true}
              isFilteredByPublishedProjects={isFilteredByPublishedProjects}
            />
          </div>
        </div>
      ) : (
        <div
          className="flex flex-col w-full px-2"
          style={{
            paddingBottom: projectGroupsAreLoading ? "0" : "0",
            paddingTop: "1.5rem",
            height:
              currentUploadsStateList.length > 0
                ? `calc(100% - 5rem - ${UPLOAD_BAR_HEIGHT})`
                : "calc(100% - 5rem)",
          }}
        >
          {}
          {!isEnduser && (
            <div className="mr-6 w-72 mb-7 h-[3rem] p-2">
              <button
                className="btn-primary-fill px-5 py-3 text-sm rounded-2xl"
                onClick={() => navigate("/courses/new")}
              >
                {t("general.newProjectGroup")}
              </button>
            </div>
          )}
          {}
          <div className="flex-column flex-grow overflow-y-auto overflow-x-hidden personalize-scroll-visible h-0 p-2">
            <div className="inline-flex justify-between w-full">
              <div
                className={`${
                  projectGroups.length === 0 ? "left-[40%]" : ""
                } inline-flex justify-between w-full flex-col items-center`}
              >
                <ProjectGroupList
                  setEditedProjectGroupId={setEditedProjectGroupId}
                  handleThreeDotButtonClick={(e: {
                    item: "project" | "projectGroup";
                    itemId: number;
                    action: string;
                  }) => {
                    setClickedProjectGroupId(e.itemId);
                    dispatch(setSelectedProjectGroupFromId(e.itemId));
                    void dispatch(
                      fetchAllCurrentElements({
                        projectGroupId: e.itemId,
                        componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
                      }),
                    );
                    handleThreeDotsButtonClickForProjectsAndProjectGroups({
                      payload: e,
                      deleteCallback: () => {
                        dispatch(openConfirmationModal("deleteProjectGroupConfirmation"));
                      },
                      t,
                      navigate,
                      dispatch: dispatch,
                    });
                  }}
                  handleClick={handleClick}
                  projectGroups={projectGroups}
                  projectGroupsWithNestedElements={projectGroupsWithNestedElements}
                  isEnduser={isEnduser}
                  searchQuery={projectGroupSearch.query}
                />
              </div>
            </div>
          </div>
          {}
          {!isEnduser && (
            <div
              className={`h-[4rem] w-full border-t ${
                loadingState == LoadingState.Loading ? "invisible" : ""
              }`}
            >
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(params) =>
                  handlePageChange({
                    ...params,
                    extraParams: { filterByPublishedProject: isFilteredByPublishedProjects },
                  })
                }
                isLoading={projectGroupsAreLoading}
              />
            </div>
          )}
        </div>
      )}

      <ModalConfirmation
        id="deleteProjectGroupConfirmation"
        onClickSubmit={() => {
          dispatch(closeConfirmationModal("deleteProjectGroupConfirmation"));

          dispatch(
            deleteProjectGroup({
              id: clickedProjectGroupId,
              componentId: `project-group-${clickedProjectGroupId}`,
            }),
          )
            .then((deleteResult) => {
              if (!deleteProjectGroup.fulfilled.match(deleteResult)) {
                return undefined;
              }
              dispatch(setAlert({ type: "success", msg: t("alerts.projectGroupDeleteSuccess") }));
              return dispatch(
                fetchProjectGroupsWithProjects({
                  start: page * limit,
                  limit,
                  sort: projectGroupSearch.sort,
                  query: projectGroupSearch.query,
                  filterByPublishedProject: isFilteredByPublishedProjects,
                  componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
                }),
              );
            })
            .then((result) => {
              if (!result) return;
              if (
                fetchProjectGroupsWithProjects.fulfilled.match(result) &&
                result.payload.data.results.length === 0 &&
                page > 0
              ) {
                dispatch(setSearchPage(page - 1));
                void dispatch(
                  fetchProjectGroupsWithProjects({
                    start: (page - 1) * limit,
                    limit,
                    sort: projectGroupSearch.sort,
                    query: projectGroupSearch.query,
                    filterByPublishedProject: isFilteredByPublishedProjects,
                    componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
                  }),
                );
              }
            })
            .catch(() => {
              dispatch(setAlert({ type: "danger", msg: t("alerts.projectGroupDeleteFail") }));
            });
        }}
        title={t("pages.projectGroup.deletionModal.title")}
        text={t("pages.projectGroup.deletionModal.body")}
        componentId={PROJECT_GROUP_LIST_COMPONENT_ID}
      />
    </>
  );
};

export default ProjectGroupsList;
